import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { Notification } from "@tql/ui-components-react/kendo/notification";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	infoCircle,
	plusCircle,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { clsx } from "clsx";
import { format } from "date-fns";
import { useMemo } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import {
	TQL_PRIVACY_POLICY_URL,
	TQL_TERMS_URL,
} from "~/shared/constants/legal-links.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Invoice } from "~/shared/schemas/invoice-schema.ts";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { useGetCustomerAccounts } from "../hooks/use-get-customer-accounts.ts";
import { useGetCustomerPaymentMethods } from "../hooks/use-get-customer-payment-methods.ts";
import { PaymentDescription } from "./components/payment-description/payment-description.tsx";
import { PaymentModalBody } from "./components/payment-modal-body/payment-modal-body.tsx";
import { PaymentSelectedMethod } from "./components/payment-selected-method/payment-selected-method.tsx";
import { usePaymentModal } from "./hooks/use-payment-modal.ts";
import styles from "./index.module.css";
import { PaymentMethodModal } from "./payment-method-modal/index.tsx";
import { PAYMENT_CONSTRAINTS } from "./utils/constants.ts";

export { PaymentAmountCell } from "./components/payment-amount-cell/payment-amount-cell.tsx";

type PaymentModalProps = {
	onClose: () => void;
	title: string;
	select: Invoice[];
	isSubmitting?: boolean;
	onRemoveInvoice?: (invoiceId: string) => void;
	onExecutePayment?: (data: {
		amounts: number;
		invoiceAmounts: Record<string, number>;
		description: string;
		isLumpSum: boolean;
	}) => void | Promise<void>;
	selectedPaymentValue: SavedCard | SavedAch | PaymentNonce | null;
	handleApplyPaymentMethod: (
		paymentMethod: SavedCard | SavedAch | PaymentNonce | null,
	) => void;
	showIneligibleNotice?: boolean;
	onDismissIneligibleNotice?: () => void;
};

export const PaymentModal = ({
	onClose,
	title,
	select,
	isSubmitting = false,
	onRemoveInvoice,
	onExecutePayment,
	selectedPaymentValue,
	handleApplyPaymentMethod,
	showIneligibleNotice = false,
	onDismissIneligibleNotice,
}: PaymentModalProps) => {
	const { paymentMethods, isLoading: isLoadingPaymentMethods } =
		useGetCustomerPaymentMethods();

	const { isLoading: isLoadingAccounts, accounts } = useGetCustomerAccounts();
	const paymentAccounts = accounts?.paymentAccounts;
	const isMobile = useIsMobile();

	const { defaultAch, defaultCreditCard } = useMemo(
		() => ({
			defaultAch:
				((paymentAccounts ?? []).find(
					(a) => a.paymentMethod === "ACH" && a.isDefault,
				) as SavedAch | undefined) ?? null,
			defaultCreditCard:
				((paymentAccounts ?? []).find(
					(a) => a.paymentMethod === "CreditCard" && a.isDefault,
				) as SavedCard | undefined) ?? null,
		}),
		[paymentAccounts],
	);

	const {
		description,
		showPaymentMethodModal,
		isLumpSumPayment,
		columns,
		effectiveAmount,
		handleClose,
		handleExecutePaymentAction,
		handleDescriptionChange,
		openPaymentMethodModal,
		closePaymentMethodModal,
		setAmounts,
		onAmountChange,
		onDeleteInvoice,
	} = usePaymentModal({
		onClose,
		title,
		select,
		onRemoveInvoice,
		onExecutePayment,
		handleApplyPaymentMethod,
	});

	if (isLoadingPaymentMethods) {
		return <LoadingSpinner />;
	}

	const achLegalText = () => {
		if (!selectedPaymentValue) {
			return undefined;
		}
		if ("cardType" in selectedPaymentValue) {
			return (
				<p>
					By clicking &quot;Make a Payment&quot;, I authorize Total Quality
					Logistics, LLC (TQL) and/or its designated payment processor(s) to
					charge my credit/debit card (selected above) for the amount listed
					above. Further, by clicking “Make a Payment”, I consent and agree to
					TQL&apos;s{" "}
					<a href={TQL_PRIVACY_POLICY_URL} rel={"noreferrer"} target={"_blank"}>
						Privacy and Cookies Policy
					</a>{" "}
					and{" "}
					<a href={TQL_TERMS_URL} rel={"noreferrer"} target={"_blank"}>
						Terms of Use
					</a>{" "}
				</p>
			);
		}
		if ("accountType" in selectedPaymentValue) {
			const accountTypeLabel = selectedPaymentValue.accountType
				? selectedPaymentValue.accountType.charAt(0).toUpperCase() +
					selectedPaymentValue.accountType.slice(1).toLowerCase()
				: "N/A";
			const financialInstitution = selectedPaymentValue.bankName ?? "N/A";
			const accountHolderName = selectedPaymentValue.accountHolderName ?? "N/A";
			const accountNumberLast4 =
				selectedPaymentValue.accountNumberLast4 ?? "N/A";

			return (
				<p>
					By clicking &quot;Make a Payment&quot;, I authorize Braintree, a
					PayPal service, on behalf of Total Quality Logistics, LLC
					(&quot;TQL&quot;), to verify my bank account information using bank
					information and consumer reports, and I authorize TQL to initiate a
					one-time ACH/electronic debit to my account as follows: Account
					holder: {accountHolderName}, Financial Institution:{" "}
					{financialInstitution}, {accountTypeLabel} Account Number: ****{" "}
					{accountNumberLast4}, Authorization Date:{" "}
					{format(new Date(), "MM/dd/yyyy")}. Further, by clicking &quot;Make a
					Payment&quot;, I consent and agree to TQL&apos;s{" "}
					<a href={TQL_PRIVACY_POLICY_URL} rel={"noreferrer"} target={"_blank"}>
						Privacy and Cookies Policy
					</a>{" "}
					and{" "}
					<a href={TQL_TERMS_URL} rel={"noreferrer"} target={"_blank"}>
						Terms of Use
					</a>
					.
				</p>
			);
		}
		return null;
	};

	return (
		<TraxModal
			className={clsx(
				styles.paymentModalTitle,
				isMobile && styles.paymentModalMobile,
			)}
			onClose={handleClose}
			title={title}
			width={isMobile ? undefined : "50%"}
		>
			{showIneligibleNotice ? (
				<div
					data-testid={"ineligible-invoice-notice"}
					style={{ marginBottom: "1rem" }}
				>
					<Notification
						closable={true}
						onClose={onDismissIneligibleNotice}
						style={{ width: "100%" }}
						type={{ style: "info", icon: false }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
							}}
						>
							<SvgIcon icon={infoCircle} size={"medium"} />
							<span>
								<strong>Selection Updated:</strong> Paid or Pending Payment
								invoices were removed because they are not eligible for payment
								at this time.
							</span>
						</div>
					</Notification>
				</div>
			) : null}

			{Boolean(showPaymentMethodModal) && (
				<PaymentMethodModal
					availablePaymentMethods={paymentMethods}
					handleApplyPaymentMethod={handleApplyPaymentMethod}
					onClose={closePaymentMethodModal}
					selectedPaymentValue={selectedPaymentValue}
				/>
			)}

			<h4 className={styles.reviewPaymentTitle}>Review Payment</h4>
			<p>
				Please make a payment between{" "}
				{formatCurrency(PAYMENT_CONSTRAINTS.MIN_AMOUNT)} and{" "}
				{formatCurrency(PAYMENT_CONSTRAINTS.MAX_AMOUNT)}
			</p>

			<PaymentModalBody
				columns={columns}
				effectiveAmount={effectiveAmount}
				isLumpSumPayment={isLumpSumPayment}
				onAmountChange={onAmountChange}
				onDeleteInvoice={onDeleteInvoice}
				select={select}
				setAmounts={setAmounts}
			/>

			<PaymentSelectedMethod selectedPaymentValue={selectedPaymentValue} />

			{selectedPaymentValue ? (
				<Button
					id={"borderless-button"}
					onClick={() => handleApplyPaymentMethod(null)}
					svgIcon={trash_01}
				>
					Clear Payment Method
				</Button>
			) : (
				<div>
					<Button
						id={"borderless-button"}
						onClick={openPaymentMethodModal}
						svgIcon={plusCircle}
					>
						Add Payment Method
					</Button>
					{!isLoadingAccounts && defaultAch ? (
						<Button
							id={"borderless-button"}
							onClick={() => handleApplyPaymentMethod(defaultAch)}
							svgIcon={plusCircle}
						>
							Apply Default ACH
						</Button>
					) : null}
					{!isLoadingAccounts && defaultCreditCard ? (
						<Button
							id={"borderless-button"}
							onClick={() => handleApplyPaymentMethod(defaultCreditCard)}
							svgIcon={plusCircle}
						>
							Apply Default Credit Card
						</Button>
					) : null}
				</div>
			)}

			<PaymentDescription
				description={description}
				isLumpSumPayment={isLumpSumPayment}
				onDescriptionChange={handleDescriptionChange}
			/>

			{achLegalText()}
			<DialogActionsBar>
				<div className={"trax-modal-confirmation-actions"}>
					<Button onClick={handleClose} variant={"secondary"}>
						Close
					</Button>
					<Button
						disabled={!selectedPaymentValue || isSubmitting}
						id={"pay-button"}
						onClick={handleExecutePaymentAction}
					>
						{isSubmitting ? "Saving..." : "Make a Payment"}
					</Button>
				</div>
			</DialogActionsBar>
		</TraxModal>
	);
};
